import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "@/App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import MenuManagement from "./pages/admin/MenuManagement";
import ProfileSettings from "./pages/admin/ProfileSettings";
import QRCodePage from "./pages/admin/QRCodePage";
import PreviewPage from "./pages/admin/PreviewPage";
import SubscriptionPage from "./pages/admin/SubscriptionPage";
import BannerManagement from "./pages/admin/BannerManagement";
import SupportPage from "./pages/admin/SupportPage";

// Public Pages
import PublicMenuPage from "./pages/PublicMenuPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SeoLandingPage from "./pages/SeoLandingPage";

// Context and UI
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import SeoRouteManager from "@/components/SeoRouteManager";

const AuthLoadingScreen = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCFBF9] text-zinc-950">
      <div className="text-center space-y-2">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-orange-100 border-t-[#EA580C]" />
        <p className="text-sm font-medium text-zinc-500">Oturum kontrol ediliyor...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();

  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <>
      <SeoRouteManager />
      <Routes>
        {/* Public sabit sayfalar */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy_policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms_of_use" element={<TermsOfUsePage />} />
        <Route path="/qr-menu" element={<SeoLandingPage pageKey="qr-menu" />} />
        <Route path="/dijital-menu" element={<SeoLandingPage pageKey="dijital-menu" />} />
        <Route path="/dijital-vitrin" element={<SeoLandingPage pageKey="dijital-vitrin" />} />
        <Route path="/qr-menu-fiyatlari" element={<SeoLandingPage pageKey="qr-menu-fiyatlari" />} />

        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/register"
          element={
            <PublicOnlyRoute>
              <RegisterPage />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <PublicOnlyRoute>
              <ForgotPasswordPage />
            </PublicOnlyRoute>
          }
        />

        {/* Admin panel */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/menu"
          element={
            <ProtectedRoute>
              <MenuManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute>
              <ProfileSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/qr-code"
          element={
            <ProtectedRoute>
              <QRCodePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/preview"
          element={
            <ProtectedRoute>
              <PreviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/banners"
          element={
            <ProtectedRoute>
              <BannerManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/support"
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />

        {/* Eski /support linki varsa admin support'a yönlendir */}
        <Route path="/support" element={<Navigate to="/admin/support" replace />} />

        {/* Public menü */}
        <Route path="/:slug" element={<PublicMenuPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;